/**
 * カラム統合パネル (差分マージ)。
 *
 * 既存配信先にしか書き込まない (新規作成しない)。
 * ユーザーが選択した CSV カラム → 既存フィールドのペアのみマージ。
 *
 * UI フロー:
 *   1. ファイル選択 → パース
 *   2. マッチキー (メアド/氏名/両方) 選択
 *   3. 左側 (CSV列) / 右側 (既存フィールド) で対応ペアを構築 (複数可)
 *   4. 「依存データを上書き」トグル
 *   5. 実行 → mergeContactColumns callable
 */
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { ArrowRight, FileUp, Search, Upload, X } from 'lucide-react';
import Papa from 'papaparse';
import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useMailContacts } from '@/hooks/useMailContacts';
import { functions } from '@/lib/firebase';

const EMAIL_COLUMN_HINTS = [
  'email',
  'e-mail',
  'mail',
  'email address',
  'mail address',
  'メールアドレス',
  'メール',
  'メアド',
];

const NAME_COLUMN_HINTS = ['name', 'fullname', 'full name', '氏名', '名前', '担当者', '担当'];

type MatchKey = 'email' | 'name' | 'both';

/** 既存フィールドのカタログ。「targetField 識別子 → ラベル」 */
const STRUCTURED_TARGETS: { field: string; label: string; group: string }[] = [
  { field: 'lastName', label: '姓', group: '個人プロファイル' },
  { field: 'firstName', label: '名', group: '個人プロファイル' },
  { field: 'name', label: '氏名 (表示名)', group: '個人プロファイル' },
  { field: 'lastNameKana', label: '姓 (カナ)', group: '個人プロファイル' },
  { field: 'firstNameKana', label: '名 (メイ)', group: '個人プロファイル' },
  { field: 'companyName', label: '会社名', group: '所属' },
  { field: 'phone', label: '電話番号', group: '連絡先' },
  { field: 'occupation', label: '職業', group: '個人プロファイル' },
  { field: 'gender', label: '性別', group: '個人プロファイル' },
  { field: 'birthdate', label: '誕生日 / 生年月日', group: '個人プロファイル' },
  { field: 'personalAddress.postalCode', label: '郵便番号', group: '住所' },
  { field: 'personalAddress.prefecture', label: '都道府県', group: '住所' },
  { field: 'personalAddress.city', label: '市区町村', group: '住所' },
  { field: 'personalAddress.line', label: '町名・番地', group: '住所' },
];

interface FieldMapping {
  id: string;
  csvColumn: string;
  targetField: string;
}

interface ParsedFile {
  headers: string[];
  rows: Array<Record<string, string>>;
  fileName: string;
}

interface MergeSummary {
  totalRows: number;
  matchedAudience: number;
  notFound: number;
  fieldUpdates: number;
  fieldSkippedExisting: number;
  contactsUpdated: number;
}

const mergeContactColumnsFn = httpsCallable<
  {
    rows: Array<Record<string, string>>;
    matchKey: MatchKey;
    emailKey?: string;
    nameKey?: string;
    mappings: { csvColumn: string; targetField: string }[];
    overwriteExisting: boolean;
  },
  { ok: boolean; summary: MergeSummary }
>(functions, 'mergeContactColumns');

function autoDetectColumn(headers: string[], hints: string[]): string {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const hint of hints) {
    const idx = lower.indexOf(hint.toLowerCase());
    if (idx >= 0) return headers[idx];
  }
  for (const hint of hints) {
    const idx = lower.findIndex((h) => h.includes(hint.toLowerCase()));
    if (idx >= 0) return headers[idx];
  }
  return '';
}

let nextMappingId = 0;
const newMappingId = () => `m_${++nextMappingId}`;

export function ColumnMergePanel({ onDone }: { onDone?: () => void }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<MergeSummary | null>(null);

  const [matchKey, setMatchKey] = useState<MatchKey>('both');
  const [emailKey, setEmailKey] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);

  /** クリック順序ベースのペア選択用 state */
  const [pending, setPending] = useState<
    { side: 'csv' | 'target'; value: string } | null
  >(null);

  /** 左右リストの検索フィルタ */
  const [csvFilter, setCsvFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');

  // 既存配信先から customFields キーを抽出して、ターゲット候補に追加
  const { data: existingContacts } = useMailContacts({ limit: 500 });
  const customFieldKeys = useMemo(() => {
    const set = new Set<string>();
    for (const c of existingContacts ?? []) {
      for (const k of Object.keys(c.customFields ?? {})) {
        if (k.trim()) set.add(k);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [existingContacts]);

  const allTargets = useMemo(() => {
    const list: { field: string; label: string; group: string }[] = [
      ...STRUCTURED_TARGETS,
    ];
    for (const k of customFieldKeys) {
      list.push({
        field: `customFields.${k}`,
        label: k,
        group: 'カスタムフィールド',
      });
    }
    return list;
  }, [customFieldKeys]);

  const reset = () => {
    setParsed(null);
    setEmailKey('');
    setNameKey('');
    setMatchKey('both');
    setMappings([]);
    setOverwriteExisting(false);
    setSummary(null);
    setPending(null);
    setCsvFilter('');
    setTargetFilter('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const applyParsed = (
    headers: string[],
    rows: Array<Record<string, string>>,
    fileName: string,
  ) => {
    if (headers.length === 0 || rows.length === 0) {
      toast.error('有効なヘッダ／データ行が見つかりません');
      return;
    }
    setParsed({ headers, rows, fileName });
    setEmailKey(autoDetectColumn(headers, EMAIL_COLUMN_HINTS));
    setNameKey(autoDetectColumn(headers, NAME_COLUMN_HINTS));
    // 新方式: 初期は空。下のリストからクリックでペア化していく
    setMappings([]);
    setPending(null);
    setCsvFilter('');
    setTargetFilter('');
  };

  const parseXlsx = async (file: File) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet) {
      toast.error('シートが見つかりません');
      return;
    }
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      blankrows: false,
    });
    if (raw.length < 2) {
      toast.error('データ行がありません');
      return;
    }
    const headerRow = (raw[0] as unknown[]).map((c) => String(c ?? '').trim());
    const seen = new Map<string, number>();
    const headers = headerRow.map((h) => {
      if (!h) return '__col_' + seen.size;
      const n = seen.get(h) ?? 0;
      seen.set(h, n + 1);
      return n === 0 ? h : `${h}_${n + 1}`;
    });
    const toStr = (v: unknown): string => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      if (v instanceof Date) return v.toISOString();
      return String(v);
    };
    const rows: Array<Record<string, string>> = [];
    for (let i = 1; i < raw.length; i++) {
      const r = raw[i] as unknown[];
      const obj: Record<string, string> = {};
      let hasValue = false;
      for (let j = 0; j < headers.length; j++) {
        const val = toStr(r?.[j]);
        if (val !== '') hasValue = true;
        obj[headers[j]] = val;
      }
      if (hasValue) rows.push(obj);
    }
    applyParsed(headers, rows, file.name);
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setSummary(null);
    const name = file.name.toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls');
    try {
      if (isXlsx) {
        await parseXlsx(file);
        setParsing(false);
        return;
      }
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setParsing(false);
          if (result.errors.length > 0) {
            toast.error(`CSV パース失敗: ${result.errors[0].message}`);
            return;
          }
          const headers = result.meta.fields ?? [];
          const rows = result.data.filter(
            (r): r is Record<string, string> =>
              r !== null && typeof r === 'object',
          );
          applyParsed(headers, rows, file.name);
        },
        error: (err) => {
          setParsing(false);
          toast.error(`CSV 読み込み失敗: ${err.message}`);
        },
      });
    } catch (err) {
      setParsing(false);
      toast.error(
        `ファイル読み込み失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const removeMapping = (id: string) => {
    setMappings((prev) => prev.filter((m) => m.id !== id));
  };

  /** 使用中のカラム/フィールドを薄表示するためのインデックス */
  const usedCsvColumns = new Set(mappings.map((m) => m.csvColumn).filter(Boolean));
  const usedTargets = new Set(mappings.map((m) => m.targetField).filter(Boolean));

  /** リストの行クリック処理。1 回目で pending に保存、反対側で 2 回目クリックでペア成立。同じ側を再クリックで解除。 */
  const handleColumnClick = (side: 'csv' | 'target', value: string) => {
    // 既にペア済みなら無視
    if (side === 'csv' && usedCsvColumns.has(value)) return;
    if (side === 'target' && usedTargets.has(value)) return;

    if (!pending) {
      setPending({ side, value });
      return;
    }

    if (pending.side === side) {
      // 同じ側を再クリック → 別の値なら無視 (グレーアウトされているはずだが念のため)、同じ値なら選択解除
      if (pending.value === value) setPending(null);
      return;
    }

    // 反対側 → ペア成立
    const csvColumn = side === 'csv' ? value : pending.value;
    const targetField = side === 'target' ? value : pending.value;
    setMappings((prev) => [
      ...prev,
      { id: newMappingId(), csvColumn, targetField },
    ]);
    setPending(null);
  };

  const canSubmit = useMemo(() => {
    if (!parsed) return false;
    if ((matchKey === 'email' || matchKey === 'both') && !emailKey) return false;
    if ((matchKey === 'name' || matchKey === 'both') && !nameKey) return false;
    const valid = mappings.filter((m) => m.csvColumn && m.targetField);
    return valid.length > 0;
  }, [parsed, matchKey, emailKey, nameKey, mappings]);

  const handleSubmit = async () => {
    if (!parsed || !canSubmit) return;
    const validMappings = mappings.filter((m) => m.csvColumn && m.targetField);
    setSubmitting(true);
    try {
      // ペイロード削減: Cloud Functions の 10MB 制限を回避するため、
      // 行から「マッチキー + マッピング対象列」のみ抽出して送信
      const neededColumns = new Set<string>();
      if (emailKey) neededColumns.add(emailKey);
      if (nameKey) neededColumns.add(nameKey);
      for (const m of validMappings) neededColumns.add(m.csvColumn);
      const slimRows = parsed.rows.map((r) => {
        const out: Record<string, string> = {};
        for (const k of neededColumns) {
          if (k in r) out[k] = r[k];
        }
        return out;
      });

      const res = await mergeContactColumnsFn({
        rows: slimRows,
        matchKey,
        emailKey: emailKey || undefined,
        nameKey: nameKey || undefined,
        mappings: validMappings.map((m) => ({
          csvColumn: m.csvColumn,
          targetField: m.targetField,
        })),
        overwriteExisting,
      });
      setSummary(res.data.summary);
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
      toast.success(
        `${res.data.summary.contactsUpdated} 件の配信先を更新 (フィールド更新 ${res.data.summary.fieldUpdates} 件)`,
      );
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (summary) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="入力行数" value={summary.totalRows} />
          <Stat label="既存一致" value={summary.matchedAudience} accent="purple" />
          <Stat label="未マッチ" value={summary.notFound} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Stat
            label="更新フィールド"
            value={summary.fieldUpdates}
            accent="green"
          />
          <Stat
            label="既存値スキップ"
            value={summary.fieldSkippedExisting}
          />
          <Stat
            label="更新済配信先"
            value={summary.contactsUpdated}
            accent="accent"
          />
        </div>
        <p className="text-[11px] text-[var(--text-muted)]">
          {summary.notFound > 0
            ? `${summary.notFound} 行は既存配信先と一致せずスキップされました (新規作成は行いません)。`
            : '入力行はすべて既存配信先と紐付きました。'}
          {summary.fieldSkippedExisting > 0 &&
            ` ${summary.fieldSkippedExisting} 件のフィールドは既存値があるためスキップされました (上書きトグル OFF)。`}
        </p>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              reset();
              onDone?.();
            }}
          >
            閉じる
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!parsed && (
        <div
          className="border-2 border-dashed border-[var(--border)] rounded-lg p-10 text-center hover:border-[var(--accent)] transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            const n = file?.name.toLowerCase() ?? '';
            if (file && (n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.xls'))) {
              handleFile(file);
            } else {
              toast.error('.csv / .xlsx / .xls ファイルをドロップしてください');
            }
          }}
        >
          <FileUp className="size-8 mx-auto text-[var(--text-muted)] mb-2" />
          <div className="text-sm text-[var(--text-secondary)]">
            クリックまたはドラッグ＆ドロップで CSV / Excel を選択
          </div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1">
            既存配信先の指定フィールドにだけ値を上書き / 追記します (新規作成はしません)
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onFileChange}
            className="hidden"
          />
          {parsing && (
            <div className="flex items-center justify-center gap-2 mt-3 text-xs text-[var(--text-muted)]">
              <Spinner className="size-3" /> パース中…
            </div>
          )}
        </div>
      )}

      {parsed && (
        <>
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <FileUp className="size-3.5" />
            <span className="font-medium text-[var(--text-secondary)]">
              {parsed.fileName}
            </span>
            <span>· {parsed.rows.length} 行 / {parsed.headers.length} 列</span>
            <button
              type="button"
              className="ml-auto text-[var(--accent)] hover:underline"
              onClick={reset}
            >
              別のファイルを選択
            </button>
          </div>

          {/* マッチキー */}
          <div>
            <Label className="text-xs">
              統合先の配信先を特定するマッチキー
            </Label>
            <div className="flex gap-1.5 mt-1">
              {(
                [
                  { v: 'both', label: 'メアド + 氏名' },
                  { v: 'email', label: 'メアドのみ' },
                  { v: 'name', label: '氏名のみ' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMatchKey(opt.v)}
                  className={`px-3 py-1 text-xs rounded border transition-colors ${
                    matchKey === opt.v
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                      : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {(matchKey === 'email' || matchKey === 'both') && (
              <div>
                <Label className="text-xs">メアド列</Label>
                <select
                  value={emailKey}
                  onChange={(e) => setEmailKey(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 text-xs rounded bg-[var(--bg-input)] border border-[var(--border)]"
                >
                  <option value="">メアド列を選択</option>
                  {parsed.headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            )}
            {(matchKey === 'name' || matchKey === 'both') && (
              <div>
                <Label className="text-xs">氏名列</Label>
                <select
                  value={nameKey}
                  onChange={(e) => setNameKey(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 text-xs rounded bg-[var(--bg-input)] border border-[var(--border)]"
                >
                  <option value="">氏名列を選択</option>
                  {parsed.headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* ─── セクション 1: 確定ペア一覧 ─── */}
          <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-3">
            <div className="mb-2">
              <Label className="text-xs font-semibold">
                確定ペア ({mappings.length})
              </Label>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                統合する「データ元カラム → 既存フィールド」の対応一覧。下のリストから 1 つずつクリックしてペア化してください。
              </p>
            </div>
            {mappings.length === 0 ? (
              <p className="text-[11px] text-[var(--text-muted)] italic py-2">
                まだペアがありません。下のリストから 1 行ずつクリックして作成してください。
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {mappings.map((m) => {
                  const target = allTargets.find((t) => t.field === m.targetField);
                  return (
                    <div
                      key={m.id}
                      className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-[var(--bg-elevated)] border border-[var(--border)]"
                    >
                      <span className="flex-1 min-w-0 truncate text-[var(--accent)] font-medium">
                        {m.csvColumn}
                      </span>
                      <ArrowRight className="size-3 text-[var(--text-muted)] flex-shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-[var(--accent-purple)] font-medium">
                        {target?.label ?? m.targetField}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeMapping(m.id)}
                        className="text-[var(--text-muted)] hover:text-[var(--accent-red)] flex-shrink-0"
                        title="このペアを解除"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── セクション 2: ペア選択用の並列リスト ─── */}
          <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <Label className="text-xs font-semibold">
                  ペア選択
                </Label>
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                  {pending
                    ? pending.side === 'csv'
                      ? `「${pending.value}」を選択中。右の既存フィールドから 1 つクリックしてペア化、もしくは再クリックで解除。`
                      : `「${allTargets.find((t) => t.field === pending.value)?.label ?? pending.value}」を選択中。左の CSV カラムから 1 つクリックしてペア化、もしくは再クリックで解除。`
                    : '左 (CSV カラム) と 右 (既存フィールド) を交互にクリックしてペア化します。'}
                </p>
              </div>
              {pending && (
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="text-[10px] text-[var(--accent)] hover:underline"
                >
                  選択解除
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* 左: CSV カラム */}
              <div className="rounded border border-[var(--border)] overflow-hidden">
                <div className="px-2 py-1.5 bg-[var(--bg-elevated)] border-b border-[var(--border)] flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent)]">
                    データ元カラム
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)]">
                    ({parsed.headers.length})
                  </span>
                  <div className="relative ml-auto flex-1 max-w-[140px]">
                    <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 size-3 text-[var(--text-muted)] pointer-events-none" />
                    <input
                      type="search"
                      value={csvFilter}
                      onChange={(e) => setCsvFilter(e.target.value)}
                      placeholder="検索"
                      className="w-full pl-6 pr-1.5 py-0.5 text-[11px] rounded bg-[var(--bg-input)] border border-[var(--border)]"
                    />
                  </div>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {parsed.headers
                    .filter(
                      (h) =>
                        !csvFilter.trim() ||
                        h.toLowerCase().includes(csvFilter.trim().toLowerCase()),
                    )
                    .map((h) => {
                      const isPaired = usedCsvColumns.has(h);
                      const isPending =
                        pending?.side === 'csv' && pending.value === h;
                      const pairedTo = isPaired
                        ? allTargets.find(
                            (t) =>
                              t.field ===
                              mappings.find((m) => m.csvColumn === h)?.targetField,
                          )?.label
                        : null;
                      // pending が同じ csv 側で別の値 → グレーアウト & クリック不可
                      const isDimmed =
                        !!pending && pending.side === 'csv' && !isPending;
                      const disabled = isPaired || isDimmed;
                      return (
                        <button
                          key={h}
                          type="button"
                          onClick={() => handleColumnClick('csv', h)}
                          disabled={disabled}
                          className={[
                            'w-full text-left px-2 py-1.5 text-xs border-b border-[var(--border)] last:border-b-0 flex items-center gap-2 transition-colors',
                            isPending
                              ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-semibold border-l-2 border-l-[var(--accent)]'
                              : isPaired
                                ? 'bg-[var(--accent-subtle)] text-[var(--accent)] opacity-60 cursor-not-allowed'
                                : isDimmed
                                  ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                                  : 'hover:bg-[var(--bg-hover)]',
                          ].join(' ')}
                        >
                          <span className="flex-1 truncate">{h}</span>
                          {pairedTo && (
                            <span className="text-[10px] text-[var(--accent-purple)] truncate max-w-[120px]">
                              → {pairedTo}
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* 右: 既存フィールド */}
              <div className="rounded border border-[var(--border)] overflow-hidden">
                <div className="px-2 py-1.5 bg-[var(--bg-elevated)] border-b border-[var(--border)] flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-purple)]">
                    既存フィールド
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)]">
                    ({allTargets.length})
                  </span>
                  <div className="relative ml-auto flex-1 max-w-[140px]">
                    <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 size-3 text-[var(--text-muted)] pointer-events-none" />
                    <input
                      type="search"
                      value={targetFilter}
                      onChange={(e) => setTargetFilter(e.target.value)}
                      placeholder="検索"
                      className="w-full pl-6 pr-1.5 py-0.5 text-[11px] rounded bg-[var(--bg-input)] border border-[var(--border)]"
                    />
                  </div>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {(() => {
                    const filtered = allTargets.filter(
                      (t) =>
                        !targetFilter.trim() ||
                        t.label
                          .toLowerCase()
                          .includes(targetFilter.trim().toLowerCase()) ||
                        t.field
                          .toLowerCase()
                          .includes(targetFilter.trim().toLowerCase()),
                    );
                    const grouped = groupTargets(filtered);
                    return Object.entries(grouped).map(([group, items]) => (
                      <div key={group}>
                        <div className="px-2 py-1 bg-[var(--bg-elevated)] text-[10px] uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)]">
                          {group}
                        </div>
                        {items.map((t) => {
                          const isPaired = usedTargets.has(t.field);
                          const isPending =
                            pending?.side === 'target' && pending.value === t.field;
                          const pairedFrom = isPaired
                            ? mappings.find((m) => m.targetField === t.field)
                                ?.csvColumn
                            : null;
                          const isDimmed =
                            !!pending && pending.side === 'target' && !isPending;
                          const disabled = isPaired || isDimmed;
                          return (
                            <button
                              key={t.field}
                              type="button"
                              onClick={() => handleColumnClick('target', t.field)}
                              disabled={disabled}
                              className={[
                                'w-full text-left px-2 py-1.5 text-xs border-b border-[var(--border)] last:border-b-0 flex items-center gap-2 transition-colors',
                                isPending
                                  ? 'bg-[var(--accent-purple-subtle,_rgba(155,93,229,0.15))] text-[var(--accent-purple)] font-semibold border-l-2 border-l-[var(--accent-purple)]'
                                  : isPaired
                                    ? 'bg-[var(--accent-purple-subtle,_rgba(155,93,229,0.15))] text-[var(--accent-purple)] opacity-60 cursor-not-allowed'
                                    : isDimmed
                                      ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                                      : 'hover:bg-[var(--bg-hover)]',
                              ].join(' ')}
                            >
                              <span className="flex-1 truncate">{t.label}</span>
                              {pairedFrom && (
                                <span className="text-[10px] text-[var(--accent)] truncate max-w-[120px]">
                                  ← {pairedFrom}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* 上書きトグル */}
          <div className="flex items-center justify-between gap-3 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
            <div className="flex-1 min-w-0">
              <Label className="text-xs font-semibold">
                依存データを上書き
              </Label>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                オンの場合、統合先のフィールドに既に値があってもデータ元で上書きします。
                オフの場合、既存値があるフィールドはスキップされます (空のフィールドのみ書き込み)。
              </p>
            </div>
            <Switch
              checked={overwriteExisting}
              onChange={(e) => setOverwriteExisting(e.target.checked)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? <Spinner className="size-3 mr-1" /> : <Upload className="size-3.5 mr-1" />}
              統合を実行
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function groupTargets(
  targets: { field: string; label: string; group: string }[],
): Record<string, { field: string; label: string; group: string }[]> {
  const out: Record<string, { field: string; label: string; group: string }[]> =
    {};
  for (const t of targets) {
    if (!out[t.group]) out[t.group] = [];
    out[t.group].push(t);
  }
  return out;
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'accent' | 'purple' | 'green';
}) {
  const color =
    accent === 'accent'
      ? 'text-[var(--accent)]'
      : accent === 'purple'
        ? 'text-[var(--accent-purple)]'
        : accent === 'green'
          ? 'text-[var(--accent-green)]'
          : 'text-[var(--text-primary)]';
  return (
    <div className="p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{label}</div>
    </div>
  );
}

